import { useState, useEffect, useCallback } from 'react';
import {
  getTodayDate,
  getTomorrowDate,
  type DailyServicePlan,
  type HousekeepingTask,
  type RoomServicePlan,
  type ServiceMode
} from '../services/housekeepingPlanner';
import { fetchHousekeepingTasks, type ApiHousekeepingTask, type TaskOrigin } from '@/api';

export type PlannerView = 'today' | 'tomorrow';

interface UseHousekeepingPlannerState {
  plan: DailyServicePlan | null;
  loading: boolean;
  error: string | null;
  selectedView: PlannerView;
  refreshing: boolean;
}

interface UseHousekeepingPlannerReturn extends UseHousekeepingPlannerState {
  switchToToday: () => void;
  switchToTomorrow: () => void;
  refresh: () => Promise<void>;
}

/**
 * React hook for housekeeping daily service planner
 * Phase P2.4-6: PMS-Centric Architecture
 *
 * Now queries backend /api/housekeeping/generate-tasks which reads from PMS
 * - Guest names from PMS
 * - Green stay preferences from PMS
 * - Room status from PMS
 *
 * Features:
 * - Loads and generates daily service plans from PMS
 * - Auto-refreshes every 60 seconds
 * - Supports today/tomorrow view switching
 *
 * @param rooms - Array of room numbers to plan for
 */
export function useHousekeepingPlanner(rooms: string[]): UseHousekeepingPlannerReturn {
  const [state, setState] = useState<UseHousekeepingPlannerState>({
    plan: null,
    loading: true,
    error: null,
    selectedView: 'today',
    refreshing: false,
  });

  /**
   * Fetch tasks for the currently selected date
   * Phase P2.5-1: Uses new PMS-driven API with origin, priority, status
   */
  const generatePlan = useCallback(async (isRefresh: boolean = false, viewOverride?: PlannerView) => {
    const currentView = viewOverride ?? state.selectedView;

    if (!isRefresh) {
      setState(prev => ({ ...prev, loading: true, error: null }));
    } else {
      setState(prev => ({ ...prev, refreshing: true }));
    }

    try {
      // Determine target date based on view
      const targetDate = currentView === 'today' ? getTodayDate() : getTomorrowDate();

      // Fetch existing tasks from backend (Phase P2.5-1: includes lifecycle events)
      const response = await fetchHousekeepingTasks(targetDate);

      // Transform API tasks to HousekeepingTask format (preserve origin data)
      const tasks: HousekeepingTask[] = response.tasks.map((task: ApiHousekeepingTask) => ({
        id: task.taskId,
        roomNumber: task.roomNumber,
        guestName: undefined, // TODO: Add guest name from PMS context
        date: task.date,
        type: task.type === 'SKIPPED' ? 'SKIPPED' : task.type === 'TOWELS_ONLY' ? 'TOWELS_ONLY' : 'FULL_CLEAN',
        status: task.status, // Phase P2.5-1: Preserve task status for workflow buttons
        priority: task.priority as any, // Pass through HIGH, MEDIUM, NORMAL, LOW
        source: mapOriginToSource(task.origin),
        estimatedDuration: task.estimatedDuration,
        greenStayEnabled: task.origin === 'GREEN_STAY',
        notes: task.notes,
        // Phase P2.5-1: Preserve origin for UI badges
        origin: task.origin,
      }));

      // Build room service plans for ALL rooms (not just rooms with tasks)
      const allRooms = rooms; // From hook parameter (101-405)
      const roomPlans: RoomServicePlan[] = allRooms.map((roomNumber) => {
        // Find relevant tasks: active work (PENDING/IN_PROGRESS) or green stay skips (CANCELLED+SKIPPED)
        // SKIPPED tasks have status=CANCELLED (no cleaning needed) but we still need them for green stay badges
        const task = response.tasks.find((t: ApiHousekeepingTask) =>
          t.roomNumber === roomNumber &&
          t.status !== 'COMPLETED' &&
          (t.status !== 'CANCELLED' || t.type === 'SKIPPED')
        );

        if (!task) {
          // No active task for this room = Room is clean (no work needed)
          return {
            roomNumber,
            date: targetDate,
            serviceMode: 'NO_SERVICE',  // Changed from FULL_CLEAN - means no work needed
            priority: 'LOW' as any,
            source: 'DEFAULT',
            notes: [],
          };
        }

        let serviceMode: ServiceMode;
        if (task.blocked || task.status === 'BLOCKED') {
          // Distinguish CHECKOUT_TODAY from OUT_OF_ORDER
          if (task.type === 'CHECKOUT_CLEAN' && task.cancelReason === 'AWAITING_CHECKOUT') {
            serviceMode = 'CHECKOUT_TODAY';
          } else {
            serviceMode = 'OUT_OF_ORDER';
          }
        }
        else if (task.type === 'CHECKOUT_CLEAN') {
          // Unblocked CHECKOUT_CLEAN = tomorrow planning task (guest checks out on that day)
          serviceMode = 'CHECKOUT_TODAY';
        }
        else if (task.type === 'SKIPPED') serviceMode = 'NO_SERVICE';
        else if (task.type === 'TOWELS_ONLY') serviceMode = 'TOWELS_ONLY';
        else serviceMode = 'FULL_CLEAN';

        const roomPlan = {
          roomNumber: task.roomNumber,
          date: task.date,
          serviceMode,
          priority: task.priority as any,
          source: mapOriginToSource(task.origin),
          notes: task.notes ? [task.notes] : [],
        };

        return roomPlan;
      });

      // Calculate workload stats (exclude COMPLETED tasks - only count active work)
      const activeTasks = response.tasks.filter((t: ApiHousekeepingTask) =>
        t.status !== 'COMPLETED' && t.status !== 'CANCELLED'
      );
      const fullClean = activeTasks.filter((t: ApiHousekeepingTask) =>
        t.type === 'FULL_CLEAN'
      ).length;
      const towelsOnly = activeTasks.filter((t: ApiHousekeepingTask) =>
        t.type === 'TOWELS_ONLY'
      ).length;
      const skip = response.tasks.filter((t: ApiHousekeepingTask) =>
        t.type === 'SKIPPED'
      ).length;
      const outOfOrder = activeTasks.filter((t: ApiHousekeepingTask) =>
        (t.blocked || t.status === 'BLOCKED') &&
        !(t.cancelReason === 'AWAITING_CHECKOUT')
      ).length;
      const checkoutToday = activeTasks.filter((t: ApiHousekeepingTask) =>
        t.type === 'CHECKOUT_CLEAN' &&
        (t.blocked && t.cancelReason === 'AWAITING_CHECKOUT' || !t.blocked)
      ).length;

      const workload = {
        fullClean,
        towelsOnly,
        skip,
        outOfOrder,
        checkoutToday,
        total: fullClean + towelsOnly + skip,
      };

      const plan: DailyServicePlan = {
        date: targetDate,
        generatedAt: new Date(),
        rooms: roomPlans,
        tasks: tasks,
        workload,
        isTomorrow: currentView === 'tomorrow',
      };

      console.log('[Housekeeping] ✓ Tasks fetched from PMS:', {
        date: targetDate,
        tasks: plan.tasks.length,
        highPriority: response.tasks.filter((t: ApiHousekeepingTask) => t.priority === 'HIGH').length,
        workload,
      });

      setState(prev => ({
        ...prev,
        plan,
        loading: false,
        error: null,
        refreshing: false,
      }));
    } catch (err) {
      console.error('[Housekeeping] Failed to fetch tasks:', err);
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch tasks',
        refreshing: false,
      }));
    }
  }, [rooms, state.selectedView]);

  /**
   * Map TaskOrigin to TaskSource for backward compatibility
   */
  function mapOriginToSource(origin: TaskOrigin): 'MAINTENANCE' | 'GREEN_STAY' | 'GUEST_REQUEST' | 'DEFAULT' {
    switch (origin) {
      case 'PMS_CHECKOUT':
        return 'DEFAULT';
      case 'MAINTENANCE':
        return 'MAINTENANCE';
      case 'GREEN_STAY':
        return 'GREEN_STAY';
      case 'GUEST_REQUEST':
        return 'GUEST_REQUEST';
      default:
        return 'DEFAULT';
    }
  }

  /**
   * Manual refresh trigger
   */
  const refresh = useCallback(async () => {
    await generatePlan(true);
  }, [generatePlan]);

  /**
   * Switch to today view
   */
  const switchToToday = useCallback(() => {
    setState(prev => ({ ...prev, selectedView: 'today' }));
  }, []);

  /**
   * Switch to tomorrow view
   */
  const switchToTomorrow = useCallback(() => {
    setState(prev => ({ ...prev, selectedView: 'tomorrow' }));
  }, []);

  // Generate plan on mount and when view changes
  useEffect(() => {
    generatePlan(false, state.selectedView);
  }, [state.selectedView, rooms.length, generatePlan]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      generatePlan(true);
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [generatePlan]);

  // Listen for eco events changes from other windows (guest dashboard)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'hotel-concierge-eco-events' && e.newValue) {
        console.log('[HousekeepingPlanner] Eco events updated from another window, refreshing plan');
        generatePlan(true);
      }
    };

    // Listen for native storage events (from other windows/tabs)
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [generatePlan]);

  // Listen for same-window eco events updates (custom event)
  useEffect(() => {
    const handleCustomEvent = () => {
      console.log('[HousekeepingPlanner] Eco events updated in same window, refreshing plan');
      generatePlan(true);
    };

    window.addEventListener('eco-events-updated', handleCustomEvent);

    return () => {
      window.removeEventListener('eco-events-updated', handleCustomEvent);
    };
  }, [generatePlan]);

  return {
    plan: state.plan,
    loading: state.loading,
    error: state.error,
    selectedView: state.selectedView,
    refreshing: state.refreshing,
    switchToToday,
    switchToTomorrow,
    refresh,
  };
}
